import { useEffect, useState } from 'react';
import { fetchDashboardAnalyticsData } from '~/api';
import { selectUser } from '~/selectors';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { getCurrentYearMonths } from '~/utils/datetime';

const defaultCurrency = 'USD';

type ChartPoint = {
  name: string;
  value: number;
};

const generateEmptyData = (): ChartPoint[] => {
  return getCurrentYearMonths().map((month: string) => ({
    name: month,
    value: 0,
  }));
};

const useAnalyticsChartData = () => {
  const user = useSelector(selectUser);
  const businessUuid = useSelector(selectBusinessUuid);
  const currency = user.businessDetails.currency ?? defaultCurrency;

  const [analyticsType, setAnalyticsType] = useState<'payments' | 'invoiced'>('invoiced');
  const [dateRange, setDateRange] = useState(''); // format: "2025-01,2025-12"
  const [chartData, setChartData] = useState<ChartPoint[]>(generateEmptyData());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const promise = await fetchDashboardAnalyticsData(
          businessUuid,
          currency,
          analyticsType,
          dateRange,
        );
        const response = await apiResponseHandler<ChartPoint[]>(promise);

        if (response.type === ApiResponseEnum.Success) {
          setChartData(response.data);
        } else {
          setChartData(generateEmptyData());
        }
      } catch (err) {
        setChartData(generateEmptyData());
      } finally {
        setLoading(false);
      }
    };

    if (businessUuid && currency) {
      fetch();
    }
  }, [businessUuid, currency, analyticsType, dateRange]);

  return {
    chartData,
    loading,
    analyticsType,
    setAnalyticsType,
    dateRange,
    setDateRange,
    currency,
  };
};

export default useAnalyticsChartData;
